// The three marks this dashboard draws, by hand, in SVG.
//
// No chart library: the app's dependencies are React, the router and the
// Supabase client, and a 200 kB charting package to draw thirty bars would be
// the largest thing in the bundle. What is here is small because the specs are
// fixed — 2px lines, a 10% wash under them, bars capped at 24px with a 4px
// rounded data end, hairline gridlines, and no number printed on every point.
//
// COLOUR, AND WHY THERE IS SO LITTLE OF IT. Identity here is carried by labels,
// not by hue. The obvious idea — Facebook blue, Instagram magenta, YouTube red —
// fails on measurement: Instagram's magenta and YouTube's red sit ΔE 13.4 apart
// to *normal* colour vision (worse under deuteranopia), which is below the
// legibility floor for two marks that must be told apart. So the per-platform
// chart is one hue and the platform is written next to its bar. Status colours
// (published, failed) are the reserved semantic tokens and always ship with a
// word beside them, never colour alone.
//
// Every colour below is a theme token (ADR-0014). Nothing here writes a hex.

import { useId, useState } from "react";

/** Points in, path out. Plain linear interpolation — no smoothing, because a
 *  curve through daily counts invents days that did not happen. */
function line(points: Array<{ x: number; y: number }>): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ");
}

export interface SeriesPoint {
  label: string;
  value: number;
  /** The full label for the tooltip; `label` may be abbreviated for the axis. */
  title?: string;
}

/**
 * A trend over time: one series, area wash under a 2px line.
 *
 * One series, so no legend — the card's own title says what is plotted. Labels
 * are selective by design: the axis carries the ends, the tooltip carries the
 * rest, and the peak is the only point that gets a number on it.
 */
export function TrendChart({
  points, height = 132, label,
}: {
  points: SeriesPoint[];
  height?: number;
  label: string;
}) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  if (points.length === 0) return null;

  // A viewBox in abstract units with preserveAspectRatio="none" would stretch
  // the stroke; this one is drawn at a fixed internal width and scaled by CSS.
  const w = 720;
  const h = height;
  const padTop = 14;
  const padBottom = 22;
  const plot = h - padTop - padBottom;

  const max = Math.max(1, ...points.map((p) => p.value));
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const xy = points.map((p, i) => ({
    x: i * step,
    y: padTop + plot - (p.value / max) * plot,
  }));

  const peak = points.reduce((best, p, i) => (p.value > points[best].value ? i : best), 0);
  const active = hover ?? null;

  return (
    <figure className="viz" aria-label={label}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" preserveAspectRatio="none"
           onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="viz-wash-top" />
            <stop offset="100%" className="viz-wash-bottom" />
          </linearGradient>
        </defs>

        {/* Three hairlines: nothing, half, the maximum. Solid and recessive —
            a dashed grid is more ink than the data it sits behind. */}
        {[0, 0.5, 1].map((f) => (
          <line key={f} className="viz-grid"
                x1={0} x2={w}
                y1={padTop + plot - f * plot} y2={padTop + plot - f * plot} />
        ))}

        <path className="viz-area" fill={`url(#${gradientId})`}
              d={`${line(xy)} L${w} ${padTop + plot} L0 ${padTop + plot} Z`} />
        <path className="viz-line" d={line(xy)} />

        {/* The peak gets a marker and a number. Every other point gets the
            tooltip, which is what stops this becoming a wall of digits. */}
        <circle className="viz-dot" cx={xy[peak].x} cy={xy[peak].y} r={4} />
        {max > 0 && (
          <text className="viz-point-label"
                x={Math.min(w - 18, Math.max(14, xy[peak].x))}
                y={Math.max(11, xy[peak].y - 9)}
                textAnchor={xy[peak].x > w - 60 ? "end" : xy[peak].x < 60 ? "start" : "middle"}>
            {points[peak].value}
          </text>
        )}

        {active !== null && (
          <g>
            <line className="viz-crosshair" x1={xy[active].x} x2={xy[active].x}
                  y1={padTop} y2={padTop + plot} />
            <circle className="viz-dot" cx={xy[active].x} cy={xy[active].y} r={4} />
          </g>
        )}

        {/* Hit targets are the full column height, not the 8px dot. */}
        {points.map((p, i) => (
          <rect key={p.label} x={i * step - step / 2} y={0} width={step || w} height={h}
                className="viz-hit" onMouseEnter={() => setHover(i)}>
            <title>{`${p.title ?? p.label}: ${p.value}`}</title>
          </rect>
        ))}

        <text className="viz-axis" x={0} y={h - 6} textAnchor="start">{points[0].label}</text>
        <text className="viz-axis" x={w} y={h - 6} textAnchor="end">
          {points[points.length - 1].label}
        </text>
      </svg>

      <figcaption className="viz-caption muted">
        {active !== null
          ? `${points[active].title ?? points[active].label} — ${points[active].value}`
          : label}
      </figcaption>
    </figure>
  );
}

/**
 * Magnitude across a handful of named things: horizontal bars, one hue.
 *
 * Horizontal because the names are words ("Instagram"), and a column chart with
 * words under it either rotates them or truncates them. The name is the
 * identity channel, so the bars do not need to differ in colour — see the note
 * at the top of this file for why they deliberately do not.
 */
export function BarList({
  rows, valueLabel,
}: {
  rows: Array<{ label: string; value: number; note?: string | null }>;
  valueLabel: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));

  return (
    <ul className="viz-bars">
      {rows.map((r) => (
        <li key={r.label}>
          <span className="viz-bar-label">{r.label}</span>
          <span className="viz-bar-track" aria-hidden="true">
            <span className="viz-bar-fill" style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className="viz-bar-value">
            {r.value}
            <span className="visually-hidden"> {valueLabel}</span>
          </span>
          {r.note && <span className="viz-bar-note muted">{r.note}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * One ratio against its whole, as a meter rather than a two-slice pie.
 *
 * `tone` picks a reserved status colour, and the caller always renders a word
 * beside it — a bar that is only red is a bar that says nothing to a reader who
 * cannot see red.
 */
export function Meter({
  value, of, tone = "neutral",
}: {
  value: number;
  of: number;
  tone?: "neutral" | "ok" | "warn" | "err";
}) {
  const pct = of <= 0 ? 0 : Math.min(100, (value / of) * 100);
  return (
    <span className={`viz-meter tone-${tone}`} aria-hidden="true">
      <span style={{ width: `${pct}%` }} />
    </span>
  );
}
